import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFieldSelection, IssueFieldSelectionValues} from '@github-ui/issue-fields-shared/issue-field'
import {parsePinnedIssueFields, toIssueFieldType} from '@github-ui/issue-fields-shared/issue-field'
import {useIssueFieldsAnalytics} from '@github-ui/issue-fields-shared/use-issue-fields-analytics'
import {IssuesLoadingSkeleton} from '@github-ui/issues-loading-skeleton'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import {Heading} from '@primer/react'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {
  getIssueFieldSelectionValue,
  type IssueFieldWithUnsavedValue,
  toIssueFieldWithUnsavedValue,
  toIssueFieldWithValue,
} from '../../utils/issue-field-helper'
import {Section} from '../Section'
import {LABELS} from './constants/labels'
import {FieldsSelector} from './FieldSelector'
import styles from './FieldsSection.module.css'
import {
  getPinnedFieldClassName,
  getPinnedFieldPosition,
  usePendingFieldFocus,
  usePinnedFieldsAnimation,
} from './helpers'
import {usePinnedFieldsState} from './hooks/use-pinned-fields-state'
import {getEditor, getPendingEditor, type SafeFieldValue} from './IssueFieldEditors'

export type CreateIssueFieldsSectionProps = {
  owner: string
  ownerId?: number | null
  onSelectionChange: (fields: IssueFieldWithUnsavedValue[]) => void
  issueType?: IssueTypePickerIssueType | null
  orgPinnedFields?: readonly IssueFieldSelection[] | null
  /** When true, hides the "no pinned fields" message because pinned fields are still loading */
  isPinnedFieldsLoading?: boolean
}

export function CreateIssueFieldsSection({
  owner,
  ownerId,
  onSelectionChange,
  issueType,
  orgPinnedFields,
  isPinnedFieldsLoading,
}: CreateIssueFieldsSectionProps) {
  const [fields, setFields] = useState<IssueFieldWithUnsavedValue[]>([])
  const [fieldsSet, setFieldsSet] = useState<string[]>([])
  const [pendingField, setPendingField] = useState<IssueFieldSelection | null>(null)
  const {animatePinned} = usePinnedFieldsAnimation(issueType?.name || null)

  const {markInteractionStart, reportUsage} = useIssueFieldsAnalytics()
  const {setAddFieldButtonElement} = usePendingFieldFocus(pendingField)

  // Use issue type pinned fields if a type is selected, otherwise org pinned fields
  const mergedPinnedFields: readonly IssueFieldSelection[] = useMemo(
    () =>
      issueType
        ? (parsePinnedIssueFields(issueType.pinnedFields?.filter(f => f?.__typename !== '%other')) ?? [])
        : (orgPinnedFields ?? []),
    [issueType, orgPinnedFields],
  )

  const {pinnedFieldIds} = usePinnedFieldsState({
    pinnedFields: mergedPinnedFields,
    setFields,
    setFieldsSet,
  })

  const hideAddFieldButton = !isFeatureEnabled('issue_metadata_add_field_button')

  useEffect(() => {
    const fieldsToReport = fields.filter(field => {
      // Only include fields that have actual values
      return field.value
    })
    onSelectionChange(fieldsToReport)
  }, [fields, onSelectionChange])

  // Ensure fieldsSet is synchronized with all visible fields to prevent dropdown reappearance
  useEffect(() => {
    const allVisibleFieldNames = fields.map(f => f.field.name).filter((name): name is string => !!name)
    const allFieldNames = [...new Set([...allVisibleFieldNames, ...fieldsSet])]

    // Only update if there are actual differences
    if (allFieldNames.length !== fieldsSet.length || !allFieldNames.every(name => fieldsSet.includes(name))) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFieldsSet(allFieldNames)
    }
  }, [fields, fieldsSet])

  const onIssueFieldSelected = useCallback(
    (selectedField: IssueFieldSelection) => {
      markInteractionStart()

      if (!selectedField.id) return
      setPendingField(selectedField)

      if (selectedField.name) {
        setFieldsSet(prev => [...prev, selectedField.name])
      }
    },
    [markInteractionStart],
  )

  const onCommit = useCallback(
    (fieldId: string, value: IssueFieldSelectionValues) => {
      setFields(prevFields => {
        let committedField: IssueFieldWithUnsavedValue | undefined
        const updatedFields = prevFields
          // update new value
          .map(field => {
            if (field.field.id === fieldId) {
              committedField = toIssueFieldWithValue(field.field, value)
              return committedField
            }
            return field
          })
          // Keep all selected fields in state for UI (even empty), but only persist pinned fields with empty values
          .filter(f => f.value !== undefined || (f.field.id && pinnedFieldIds.has(f.field.id)))

        // remove from fieldsSet only if field is completely removed (not kept as pinned field)
        if (committedField && !committedField.value) {
          const isPinnedField = committedField.field.id && pinnedFieldIds.has(committedField.field.id)
          if (!isPinnedField) {
            setFieldsSet(prev => prev.filter(name => name !== committedField?.field.name))
          }
        }

        if (committedField && committedField.field.dataType) {
          reportUsage({
            page: 'issue#new',
            action: 'create',
            fieldId,
            fieldType: toIssueFieldType(committedField.field.dataType),
            status: 'success',
            orgId: ownerId?.toString(),
          })
        }

        return updatedFields
      })
    },
    [ownerId, pinnedFieldIds, reportUsage],
  )

  const onPendingFieldCommit = useCallback(
    (fieldId: string, fieldValue: IssueFieldSelectionValues) => {
      if (!pendingField || pendingField.id !== fieldId) return

      // Remove from pending state
      setPendingField(null)

      const fieldType = toIssueFieldType(pendingField.dataType)
      const value = getIssueFieldSelectionValue(fieldType, fieldValue)
      const isEmpty = typeof value === 'string' ? !value.trim() : !value

      // Remove from fieldsSet if field is being cancelled/removed
      if (pendingField.name && isEmpty) {
        setFieldsSet(prev => prev.filter(name => name !== pendingField.name))
        return
      }

      // If value is empty, don't save the value (field is being removed)
      if (isEmpty) {
        return
      }

      // Commit the field value
      setFields(prevFields => {
        const newFields = [...prevFields]
        if (pendingField.name && !newFields.some(f => f.field.name === pendingField.name)) {
          newFields.push(toIssueFieldWithUnsavedValue(pendingField, fieldValue))
        }
        return newFields
      })

      // Ensure the field name is in fieldsSet when successfully committed
      if (pendingField.name) {
        setFieldsSet(prev => (prev.includes(pendingField.name) ? prev : [...prev, pendingField.name]))
      }
    },
    [pendingField],
  )

  // Get all field IDs in order for position calculation
  const allFieldIds = fields.map(f => f.field.id).filter((id): id is string => !!id)

  const fieldEditors = fields.map(field => {
    if (!field.field.id) return null

    const fieldValue = typeof field.value === 'object' && field.value !== null ? {...field, ...field.value} : field
    const isPinned = pinnedFieldIds.has(field.field.id)
    const position = getPinnedFieldPosition(field.field.id, pinnedFieldIds, allFieldIds)
    return getEditor({
      fieldValue: fieldValue as SafeFieldValue,
      readonly: false,
      onCommit,
      className: getPinnedFieldClassName(isPinned, animatePinned, position),
    })
  })

  const pendingFieldEditor = pendingField
    ? (() => {
        const isPinned = pinnedFieldIds.has(pendingField.id)
        // Pending field is always added at the end
        const allIdsWithPending = pendingField.id ? [...allFieldIds, pendingField.id] : allFieldIds
        const position = pendingField.id
          ? getPinnedFieldPosition(pendingField.id, pinnedFieldIds, allIdsWithPending)
          : null
        return getPendingEditor({
          fieldSelection: pendingField,
          readonly: false,
          onCommit: onPendingFieldCommit,
          className: getPinnedFieldClassName(isPinned, animatePinned, position),
        })
      })()
    : null

  const fieldSelector = (
    <FieldsSelector
      owner={owner}
      fieldsSet={fieldsSet}
      onIssueFieldSelected={onIssueFieldSelected}
      markInteractionStart={markInteractionStart}
      disabled={pendingField !== null}
      onButtonElementReceived={setAddFieldButtonElement}
      hideAddFieldButton={hideAddFieldButton}
    />
  )

  const visibleFieldEditors = fieldEditors.filter(Boolean)

  // Show skeleton when loading or when pinned fields data exists but hasn't rendered yet
  const isWaitingForFieldsToRender = mergedPinnedFields.length > 0 && visibleFieldEditors.length === 0
  const showNoPinnedFieldsMessage = !isPinnedFieldsLoading && !isWaitingForFieldsToRender

  return (
    <>
      <Section
        sectionHeader={
          <Heading as="h3" className={styles.compactSectionHeader}>
            Fields
          </Heading>
        }
      >
        {visibleFieldEditors.length > 0 ? (
          <>{visibleFieldEditors}</>
        ) : showNoPinnedFieldsMessage ? (
          <span className={styles.compactSectionEmptyText}>{LABELS.noPinnedFieldsMessage(issueType?.name)}</span>
        ) : (
          <>
            <span className={styles.loadingRow}>
              <IssuesLoadingSkeleton height="md" width="40%" />
              <IssuesLoadingSkeleton height="md" width="60%" />
            </span>
          </>
        )}
      </Section>
      {pendingFieldEditor}
      {fieldSelector}
    </>
  )
}
